import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Product} from '../../models/product.model';
import {FormState} from '../../models/form-state.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {ProductCpiService} from '../../services/product-cpi.service';
import {ProductCpiActions} from './product-cpi.actions';
import {catchError, map, tap} from 'rxjs/operators';
import {DictionaryItem} from '../../models/dictionary.model';
import {clearFormValidationErrors, setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {HttpErrorResponse} from '@angular/common/http';
import {throwError} from 'rxjs';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {ProductActions} from '../product/product.actions';
import {ProductUploadFileError} from '../../models/validation-upload-file-errors';
import {PolicyTemplateListItem} from '../../models/policy.model';

interface ProductCpiStateModel {
    form: FormState;
    product: Product;
    policyTemplatesList: PolicyTemplateListItem[];
    availableStatuses: DictionaryItem[];
    uploadFileErrors: ProductUploadFileError[];
}

const getDefaultState = () => ({
    form: new FormState(),
    product: null,
    policyTemplatesList: null,
    availableStatuses: null,
    uploadFileErrors: null,
});

@State<ProductCpiStateModel>({
    name: ProductCpiState.STATE_NAME,
    defaults: getDefaultState()
})

@Injectable()
export class ProductCpiState {

    static readonly STATE_NAME = 'productCpi';

    constructor(
        private navService: NavService,
        private productCpiService: ProductCpiService,
    ) {
    }

    @Selector()
    static product(state) {
        return state.product;
    }

    @Selector()
    static formErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static availableStatuses(state) {
        return state.availableStatuses;
    }

    @Selector()
    static policyTemplatesList(state) {
        return state.policyTemplatesList;
    }

    @Selector()
    static uploadFileErrors(state) {
        return state.uploadFileErrors;
    }

    @Action(ProductCpiActions.GetOne)
    getProduct(ctx: StateContext<ProductCpiStateModel>, {id}: ProductCpiActions.GetOne) {
        return this.productCpiService.getOne(id)
            .pipe(
                tap(product => ctx.patchState({product: new Product(product)}))
            );
    }

    @Action(ProductCpiActions.Duplicate)
    duplicateProduct(ctx: StateContext<ProductCpiStateModel>, {id, data}: ProductCpiActions.Duplicate) {
        setFormStatus(ctx, FormStatus.PENDING, ProductCpiState.STATE_NAME);
        return this.productCpiService.duplicate(id, data).subscribe(
            (product) => this.navService.navigateByCode(NavItemCodes.PRODUCT_CPI_EDIT, {productId: product.id}),
            ({error}) => ctx.dispatch(new ProductActions.FormFailure(error))
        );
    }

    @Action(ProductCpiActions.ClearValidationErrors)
    productClearValidationErrors(ctx: StateContext<ProductCpiStateModel>, {fields = []}: ProductCpiActions.ClearValidationErrors) {
        clearFormValidationErrors(ctx, fields);
    }

    @Action(ProductCpiActions.Update)
    update(ctx: StateContext<ProductCpiStateModel>, action: ProductCpiActions.Update) {
        return this.productCpiService.update(action.product)
            .pipe(
                tap(() => {
                    this.navService.navigateByCode(NavItemCodes.PRODUCT_MANAGEMENT);
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 400) {
                        ctx.dispatch(new ProductCpiActions.FormFailure(error.error));
                    }
                    return throwError(error);
                })
            );
    }

    @Action(ProductCpiActions.Merge)
    merge(ctx: StateContext<ProductCpiStateModel>, action: ProductCpiActions.Merge) {
        return this.productCpiService.merge(action.product)
            .pipe(
                map(() => ctx.dispatch(new ProductCpiActions.Update(action.product))),
                catchError(({error}) => ctx.dispatch(new ProductCpiActions.MergeFailure(error)))
            );
    }

    @Action(ProductCpiActions.MergeFailure)
    uploadFileFailure(ctx: StateContext<ProductCpiStateModel>, {uploadFileErrors = []}: ProductCpiActions.MergeFailure) {
        ctx.patchState({
            uploadFileErrors: uploadFileErrors?.map(({sheet, message: cellError}) => {
                const [cell, message] = cellError.split(';');
                return {sheet, message, cell};
            })
        });
    }

    @Action(ProductCpiActions.FormFailure)
    addProductFailure(ctx: StateContext<ProductCpiStateModel>, {validationErrors = []}: ProductCpiActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, ProductCpiState.STATE_NAME);
    }

    @Action(ProductCpiActions.PolicyTemplatesList)
    getPolicyTemplatesListList(ctx: StateContext<ProductCpiStateModel>) {
        return this.productCpiService.getTemplates().pipe(
            tap((policyTemplatesList: PolicyTemplateListItem[]) => {
                ctx.patchState({
                    policyTemplatesList
                });
            })
        );
    }

    @Action(ProductCpiActions.AvailableStatusesList)
    getAvailableStatuses(ctx: StateContext<ProductCpiStateModel>, {id}: ProductCpiActions.AvailableStatusesList) {
        return this.productCpiService.getStatusList(id).pipe(
            tap((availableStatuses: DictionaryItem[]) => {
                ctx.patchState({
                    availableStatuses
                });
            })
        );
    }

    @Action(ProductCpiActions.Clear)
    productClear(ctx: StateContext<ProductCpiStateModel>) {
        ctx.patchState({
            product: null,
            uploadFileErrors: null,
        });
    }
}
